// Apolo IDE — useApoloLogo hook (Sprint v10 light mode).
//
// Returns the right Apolo brand mark for the current theme:
//   - dark theme   → white-on-blue mark (apolo-icon-{size}.png)
//   - light theme  → black mark (apolo-icon-dark-{size}.png)
//
// Callsites use it as:
//   const { src, srcSet } = useApoloLogo(64);
//   <img src={src} srcSet={srcSet} ... />
//
// The 2x asset is always (size * 2) so srcSet stays sharp on retina.

import { useMemo } from 'react';
import { THEMES, useTheme } from './useTheme.js';

import apoloIcon64 from '../assets/apolo-icon-64.png';
import apoloIcon128 from '../assets/apolo-icon-128.png';
import apoloIcon256 from '../assets/apolo-icon-256.png';
import apoloIconDark64 from '../assets/apolo-icon-dark-64.png';
import apoloIconDark128 from '../assets/apolo-icon-dark-128.png';
import apoloIconDark256 from '../assets/apolo-icon-dark-256.png';

const ASSETS = {
  // dark theme = white-on-blue mark (apolo-icon-*)
  dark: { 64: apoloIcon64, 128: apoloIcon128, 256: apoloIcon256 },
  // light theme = black mark (apolo-icon-dark-*)
  light: { 64: apoloIconDark64, 128: apoloIconDark128, 256: apoloIconDark256 },
};

const SUPPORTED = [64, 128];

export function useApoloLogo(size = 64) {
  const { resolved } = useTheme();
  return useMemo(() => {
    if (!SUPPORTED.includes(size)) {
      throw new Error(`useApoloLogo: unsupported size ${size}. Use 64 or 128.`);
    }
    const palette = resolved === THEMES.LIGHT ? ASSETS.light : ASSETS.dark;
    const x2Size = size * 2;
    return {
      src: palette[size],
      srcSet: `${palette[size]} 1x, ${palette[x2Size]} 2x`,
    };
  }, [resolved, size]);
}
